import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '../layouts/PageTitleContext';
import useBreadcrumbSetup from '../components/useBreadcrumbSetup';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPlus, faPen, faCopy, faTrash, faEnvelope,
    faComment, faMobileAlt, faLock,
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import FabGroup from '../components/FabGroup';
import { useAuth } from '../context/AuthProvider';
import { API_BASE_URL } from '../api';
import './MarketingTemplates.scss';

const getCanalMeta = (t) => ({
    email:     { label: t('Email'),    icon: faEnvelope,   cls: 'canal--email' },
    whatsapp:  { label: t('WhatsApp'), icon: faComment,    cls: 'canal--whatsapp' },
    sms:       { label: t('SMS'),      icon: faMobileAlt,  cls: 'canal--sms' },
});

export default function MarketingTemplates() {
    const { t } = useTranslation();
    const { setTitle, setTitleIcon } = usePageTitle();
    const navigate = useNavigate();
    const { hasRight } = useAuth();
    const canCreate = hasRight('MARKETING_CREATE');
    const canEdit = hasRight('MARKETING_EDIT');
    const canDelete = hasRight('MARKETING_DELETE');
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);

    const CANAL_META = getCanalMeta(t);

    useEffect(() => {
        setTitle(`${t('marketing_templates.title')}<span style="display:block;font-size:11px;font-weight:400;color:#9ca3af;margin-top:5px;">${t('marketing_templates.subtitle')}</span>`);
        document.title = t('marketing_templates.title');
        setTitleIcon('ti-layout-grid');
        return () => setTitleIcon(null);
    }, [setTitle, setTitleIcon, t]);

    useBreadcrumbSetup([{ label: t('Marketing'), url: '/marketing/templates' }]);

    const fetchTemplates = () => {
        axios.get(`${API_BASE_URL}/api/v1/marketing/templates`, { withCredentials: true })
            .then(({ data }) => setTemplates(data))
            .catch(console.error)
            .finally(() => setLoading(false));
    };

    useEffect(() => { fetchTemplates(); }, []);

    const handleDuplicate = async (id, e) => {
        e.stopPropagation();
        try {
            const { data } = await axios.post(
                `${API_BASE_URL}/api/v1/marketing/templates/${id}/duplicate`,
                {},
                { withCredentials: true }
            );
            toast.success(t('marketing_templates.toast.duplicated'));
            navigate(`/marketing/templates/${data.id}/edit`);
        } catch {
            toast.error(t('marketing_templates.toast.duplicate_error'));
        }
    };

    const handleDelete = async (id, nom, e) => {
        e.stopPropagation();
        if (!window.confirm(t('marketing_templates.confirm.delete', { name: nom }))) return;
        try {
            await axios.delete(`${API_BASE_URL}/api/v1/marketing/templates/${id}`, { withCredentials: true });
            toast.success(t('marketing_templates.toast.deleted'));
            fetchTemplates();
        } catch {
            toast.error(t('campaigns.toast.delete_error'));
        }
    };

    const systemTemplates = templates.filter(tpl => tpl.isSystem);
    const userTemplates   = templates.filter(tpl => !tpl.isSystem);

    const TemplateCard = ({ t: tpl }) => {
        const canal = CANAL_META[tpl.canal] ?? CANAL_META.email;
        return (
            <div
                className={`tpl-card ${tpl.isSystem ? 'tpl-card--system' : ''}`}
                onClick={() => navigate(`/marketing/templates/${tpl.id}/edit`)}
            >
                <div className="tpl-card-header">
                    <span className={`tpl-canal ${canal.cls}`}>
                        <FontAwesomeIcon icon={canal.icon} />
                        {canal.label}
                    </span>
                    {tpl.isSystem && (
                        <span className="tpl-system-badge">
                            <FontAwesomeIcon icon={faLock} /> {t('marketing_templates.badge.system')}
                        </span>
                    )}
                </div>

                <div className="tpl-card-body">
                    <h3 className="tpl-name">{tpl.nom}</h3>
                    {tpl.saison && <span className="tpl-saison">{tpl.saison}</span>}
                </div>

                <div className="tpl-card-preview">
                    {tpl.textContent
                        ? <p>{tpl.textContent.substring(0, 80)}{tpl.textContent.length > 80 ? '…' : ''}</p>
                        : <p className="tpl-no-preview">{t('marketing_templates.no_preview')}</p>
                    }
                </div>

                <div className="tpl-card-actions" onClick={e => e.stopPropagation()}>
                    {tpl.isSystem ? (
                        canCreate && (
                            <button className="tpl-btn tpl-btn--primary" onClick={e => handleDuplicate(tpl.id, e)}>
                                <FontAwesomeIcon icon={faCopy} /> {t('marketing_templates.action.duplicate')}
                            </button>
                        )
                    ) : (
                        <>
                            {canEdit && (
                                <button className="tpl-btn tpl-btn--secondary" onClick={() => navigate(`/marketing/templates/${tpl.id}/edit`)}>
                                    <FontAwesomeIcon icon={faPen} /> {t('Edit')}
                                </button>
                            )}
                            {canCreate && (
                                <button className="tpl-btn tpl-btn--ghost" onClick={e => handleDuplicate(tpl.id, e)}>
                                    <FontAwesomeIcon icon={faCopy} />
                                </button>
                            )}
                            {canDelete && (
                                <button className="tpl-btn tpl-btn--danger" onClick={e => handleDelete(tpl.id, tpl.nom, e)}>
                                    <FontAwesomeIcon icon={faTrash} />
                                </button>
                            )}
                        </>
                    )}
                </div>
            </div>
        );
    };

    if (loading) return <div className="page-content text-muted p-4">{t('Loading...')}</div>;

    return (
        <div className="page-content tpl-page">

            {userTemplates.length > 0 && (
                <section className="tpl-section">
                    <h4 className="tpl-section-title">{t('marketing_templates.section.my_templates')}</h4>
                    <div className="tpl-grid">
                        {userTemplates.map(tpl => <TemplateCard key={tpl.id} t={tpl} />)}
                    </div>
                </section>
            )}

            <section className="tpl-section">
                <h4 className="tpl-section-title">{t('marketing_templates.section.predefined_templates')}</h4>
                <div className="tpl-grid">
                    {systemTemplates.map(tpl => <TemplateCard key={tpl.id} t={tpl} />)}
                </div>
            </section>

            <FabGroup buttons={canCreate ? [
                { id: 'new', icon: faPlus, label: t('marketing_templates.action.new'), onClick: () => navigate('/marketing/templates/new') },
            ] : []} />
        </div>
    );
}
